import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import WalletGauge from '../../components/wallet/WalletGauge';
import ClientFloatingWidget from '../../components/widgets/ClientFloatingWidget';
import PaymentAlertsWidget from '../../components/widgets/PaymentAlertsWidget';
import MyBugReportsWidget from '../../components/widgets/MyBugReportsWidget';
import SatisfactionSurveyPopup from '../../components/satisfaction/SatisfactionSurveyPopup';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Wallet, 
  FolderKanban, 
  MessageSquare,
  CheckCircle2,
  Plus,
  ArrowUpRight,
  CreditCard,
  Clock,
  Instagram,
  Facebook,
  Linkedin,
  TrendingUp,
  Users,
  Eye,
  HardDrive,
  ExternalLink,
  FolderOpen,
  AlertCircle,
  FileText,
  Star,
  Euro,
  FileSignature
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import AppointmentRequestWidget from '../../components/calendar/AppointmentRequestWidget';
import { FlashInfosCarousel } from '../../components/announcements/AnnouncementComponents';
import BudgetComWidget from '../../components/widgets/BudgetComWidget';

const ClientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [projects, setProjects] = useState([]);
  const [socialStats, setSocialStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [accountManager, setAccountManager] = useState(null);
  const { theme } = useTheme();
  
  // Satisfaction survey popup
  const [showSatisfactionPopup, setShowSatisfactionPopup] = useState(false);
  
  // Rating states
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingProject, setRatingProject] = useState(null);
  const [ratings, setRatings] = useState({
    ecoute: 0,
    creativite: 0,
    delai: 0,
    budget: 0
  });
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [projectsToRate, setProjectsToRate] = useState([]);
  const [projectRequests, setProjectRequests] = useState([]);

  useEffect(() => {
    fetchData();
    
    // Check for payment success
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const [pendingActions, setPendingActions] = useState(null);
  const [pendingContracts, setPendingContracts] = useState([]);

  const fetchData = async () => {
    try {
      const [dashboardRes, walletRes, projectsRes, profileRes, pendingRes, requestsRes, contractsRes] = await Promise.all([
        api.get('/dashboard/client'),
        api.get('/wallet/'),
        api.get('/projects/'),
        api.get('/auth/me'),
        api.get('/dashboard/client/pending-actions').catch(() => ({ data: null })),
        api.get('/project-requests/').catch(() => ({ data: [] })),
        api.get('/contracts/pending-signatures').catch(() => ({ data: [] }))
      ]);
      setDashboard(dashboardRes.data);
      setWallet(walletRes.data);
      setProjects(projectsRes.data);
      setUserProfile(profileRes.data);
      setPendingActions(pendingRes.data);
      setProjectRequests(requestsRes.data || []);
      setPendingContracts(contractsRes.data || []);
      
      // Get account manager info if available
      if (profileRes.data?.account_manager_id) {
        try {
          const managerRes = await api.get(`/calendar/account-manager/${profileRes.data.account_manager_id}`);
          setAccountManager(managerRes.data);
        } catch (e) {
          console.log('Could not fetch account manager');
        }
      }
      
      // Find projects that need rating (completed but not rated yet)
      const completedProjects = (projectsRes.data || []).filter(p => 
        p.status === 'completed' || p.status === 'delivered'
      );
      // Filter to those with assigned team members
      const projectsNeedingRating = completedProjects.filter(p => 
        p.assigned_team && p.assigned_team.length > 0 && !p.client_rated
      );
      setProjectsToRate(projectsNeedingRating);
      
      // Mock social stats (in production, fetch from API)
      setSocialStats({
        instagram: { followers: 2450, growth: 12.5, engagement: 4.8 },
        facebook: { followers: 1890, growth: 5.2, engagement: 2.1 },
        linkedin: { followers: 890, growth: 8.7, engagement: 3.5 },
        lastUpdated: new Date().toISOString()
      });
      
      // Check for pending satisfaction surveys
      try {
        const surveyRes = await api.get('/satisfaction-surveys/pending');
        console.log('Satisfaction survey response:', surveyRes.data);
        if (surveyRes.data && surveyRes.data.id) {
          console.log('Showing satisfaction popup for survey:', surveyRes.data.id);
          setShowSatisfactionPopup(true);
        }
      } catch (e) {
        console.log('No pending survey or error:', e);
        // No pending survey or error - ignore
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenRating = (project) => {
    setRatingProject(project);
    setRatings({ ecoute: 0, creativite: 0, delai: 0, budget: 0 });
    setRatingComment('');
    setShowRatingModal(true);
  };

  const handleSubmitRating = async () => {
    if (!ratingProject) return;
    
    // Check that all criteria are rated
    const totalRating = ratings.ecoute + ratings.creativite + ratings.delai + ratings.budget;
    if (totalRating === 0) {
      toast.error('Veuillez noter au moins un critère');
      return;
    }
    
    setSubmittingRating(true);
    try {
      // Rate each team member assigned to the project
      const teamMembers = ratingProject.assigned_team || [];
      
      if (teamMembers.length > 0) {
        // If team members are assigned, rate each one
        for (const memberId of teamMembers) {
          await api.post('/ratings/', {
            project_id: ratingProject.id,
            creative_id: memberId,
            rating: totalRating, // Total sur 20
            ratings_detail: ratings, // Détail par critère
            comment: ratingComment
          });
        }
      } else {
        // If no team members, create a generic rating for the project
        await api.post('/ratings/', {
          project_id: ratingProject.id,
          creative_id: 'project-team', // Generic ID for project rating
          rating: totalRating,
          ratings_detail: ratings,
          comment: ratingComment
        });
      }
      
      // Mark project as rated
      await api.put(`/projects/${ratingProject.id}`, { client_rated: true });
      
      toast.success('Merci pour votre évaluation !');
      setShowRatingModal(false);
      setRatingProject(null);
      fetchData();
    } catch (error) {
      console.error('Error submitting rating:', error);
      const errorMessage = error.response?.data?.detail;
      if (typeof errorMessage === 'string') {
        toast.error(errorMessage);
      } else {
        toast.error('Erreur lors de la soumission de l\'évaluation');
      }
    } finally {
      setSubmittingRating(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await api.get(`/payments/status/${sessionId}`);
      if (response.data.payment_status === 'paid') {
        toast.success('Paiement réussi ! Vos heures ont été ajoutées.');
        fetchData();
      }
    } catch (error) {
      console.error('Error checking payment:', error);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-700',
      brief_pending: 'bg-yellow-100 text-yellow-700',
      brief_signed: 'bg-blue-100 text-blue-700',
      in_progress: 'bg-purple-100 text-purple-700',
      review: 'bg-orange-100 text-orange-700',
      delivered: 'bg-green-100 text-green-700',
      completed: 'bg-emerald-100 text-emerald-700',
    };
    const labels = {
      draft: 'Brouillon',
      brief_pending: 'Brief en attente',
      brief_signed: 'Brief validé',
      in_progress: 'En cours',
      review: 'En révision',
      delivered: 'Livré',
      completed: 'Terminé',
    };
    return (
      <Badge className={cn('font-medium', styles[status] || styles.draft)}>
        {labels[status] || status}
      </Badge>
    );
  };

  const projectTypeIcons = {
    logo: '🎨',
    visual: '🖼️',
    video: '🎬',
    website: '🌐',
    mobile_app: '📱',
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      {/* Satisfaction Survey Popup */}
      {showSatisfactionPopup && (
        <SatisfactionSurveyPopup onClose={() => setShowSatisfactionPopup(false)} />
      )}
      
      <main className={cn(
        "transition-all duration-300 px-3 md:px-6 pb-20 md:pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="mb-4 md:mb-8">
          <h1 className="text-xl md:text-3xl font-bold text-[#1a365d]" data-testid="dashboard-title">
            Bienvenue, {user?.first_name} 👋
          </h1>
          <p className="text-xs md:text-base text-[#64748b] mt-1">
            {user?.company_name && `${user.company_name} • `}Espace client
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Pending Actions Alert Banner */}
            {pendingActions && pendingActions.total_pending > 0 && (
              <Card className="card mb-6 border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="w-6 h-6 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                        Actions en attente de votre part
                        <Badge className="bg-amber-500 text-white">{pendingActions.total_pending}</Badge>
                      </h3>
                      <div className="space-y-2">
                        {pendingActions.tasks_awaiting_validation > 0 && (
                          <div 
                            className="flex items-center gap-2 text-sm text-amber-700 p-2 rounded-lg bg-white/50 hover:bg-white cursor-pointer transition-colors"
                            onClick={() => navigate('/my-projects')}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            <span><strong>{pendingActions.tasks_awaiting_validation}</strong> tâche(s) à valider</span>
                            <ArrowUpRight className="w-4 h-4 ml-auto" />
                          </div>
                        )}
                        {pendingActions.briefs_to_sign > 0 && (
                          <div 
                            className="flex items-center gap-2 text-sm text-amber-700 p-2 rounded-lg bg-white/50 hover:bg-white cursor-pointer transition-colors"
                            onClick={() => navigate('/my-projects')}
                          >
                            <FileText className="w-4 h-4" />
                            <span><strong>{pendingActions.briefs_to_sign}</strong> brief(s) à signer</span>
                            <ArrowUpRight className="w-4 h-4 ml-auto" />
                          </div>
                        )}
                        {pendingActions.projects_to_validate > 0 && (
                          <div 
                            className="flex items-center gap-2 text-sm text-amber-700 p-2 rounded-lg bg-white/50 hover:bg-white cursor-pointer transition-colors"
                            onClick={() => {
                              // Navigate to the first project that needs validation
                              if (pendingActions.projects_list && pendingActions.projects_list.length > 0) {
                                navigate(`/my-projects/${pendingActions.projects_list[0].id}`);
                              } else {
                                navigate('/my-projects');
                              }
                            }}
                          >
                            <FolderKanban className="w-4 h-4" />
                            <span><strong>{pendingActions.projects_to_validate}</strong> livrable(s) à valider</span>
                            <ArrowUpRight className="w-4 h-4 ml-auto" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pending Contracts Alert */}
            {pendingContracts.length > 0 && (
              <Card className="mb-6 border-indigo-300 bg-indigo-50">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-indigo-100 rounded-full">
                      <FileSignature className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-indigo-900">Contrats à signer</h3>
                        <Badge className="bg-indigo-500 text-white">{pendingContracts.length}</Badge>
                      </div>
                      <p className="text-indigo-700 text-sm mt-1">
                        Vous avez {pendingContracts.length} contrat{pendingContracts.length > 1 ? 's' : ''} en attente de votre signature.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-3">
                        {pendingContracts.slice(0, 3).map(contract => (
                          <Button
                            key={contract.id}
                            size="sm"
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={() => navigate('/my-contracts')}
                          >
                            <FileSignature className="w-4 h-4 mr-1" />
                            {contract.name}
                          </Button>
                        ))}
                        {pendingContracts.length > 3 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-indigo-300 text-indigo-700"
                            onClick={() => navigate('/my-contracts')}
                          >
                            +{pendingContracts.length - 3} autre{pendingContracts.length > 4 ? 's' : ''}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Flash Infos Carousel */}
            <FlashInfosCarousel className="mb-6" />

            {/* Payment Alerts & Bug Reports - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Payment Alerts Widget - Shows popup on first load if urgent */}
              <PaymentAlertsWidget showPopupOnLoad={true} />
              
              {/* My Bug Reports Widget */}
              <MyBugReportsWidget maxBugs={3} />
            </div>

            {/* Top Section - Wallet & Quick Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8" data-tour="dashboard-stats">
              {/* Wallet Card */}
              <Card className="card lg:col-span-1" data-testid="wallet-card" data-tour="wallet-card">
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                    <Wallet className="w-5 h-5" />
                    Mon Wallet
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <WalletGauge 
                    balance={wallet?.balance_hours || 0}
                    reservedHours={wallet?.reserved_hours || 0}
                    maxBalance={100}
                    hourlyRate={wallet?.hourly_rate || 75}
                    reservations={wallet?.reservations || []}
                    onClick={() => navigate('/wallet')}
                    onRegularize={() => navigate('/wallet?action=topup')}
                  />
                  <Button 
                    onClick={() => navigate('/wallet')}
                    className="btn-primary mt-6 w-full"
                    data-testid="topup-wallet-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Recharger
                  </Button>
                </CardContent>
              </Card>

              {/* Stats */}
              <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Projets actifs */}
                <Card 
                  className="card card-hover cursor-pointer" 
                  data-testid="stat-projects"
                  onClick={() => navigate('/my-projects')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#6366f1]/10 rounded-xl flex items-center justify-center">
                          <FolderKanban className="w-5 h-5 text-[#6366f1]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#64748b]">Projets actifs</p>
                          <p className="font-mono text-xl font-bold text-[#1a365d]">
                            {dashboard?.active_projects || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Preview list */}
                    <div className="space-y-2 border-t border-[#e2e8f0] pt-3">
                      {dashboard?.active_projects_list?.length > 0 ? (
                        <>
                          {dashboard.active_projects_list.map((project, idx) => (
                            <div key={project.id || idx} className="flex items-center gap-2 text-xs">
                              <span className="text-base">{projectTypeIcons[project.project_type] || '📁'}</span>
                              <span className="truncate flex-1 text-[#334155]">{project.name}</span>
                              {getStatusBadge(project.status)}
                            </div>
                          ))}
                          {dashboard.active_projects > 3 && (
                            <p className="text-xs text-[#6366f1] font-medium text-center pt-1">
                              +{dashboard.active_projects - 3} autre{dashboard.active_projects - 3 > 1 ? 's' : ''}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-[#94a3b8] italic">Aucun projet actif</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* À valider */}
                <Card 
                  className="card card-hover cursor-pointer" 
                  data-testid="stat-validations"
                  onClick={() => navigate('/my-projects?filter=review')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#f59e0b]/10 rounded-xl flex items-center justify-center">
                          <CheckCircle2 className="w-5 h-5 text-[#f59e0b]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#64748b]">À valider</p>
                          <p className="font-mono text-xl font-bold text-[#f59e0b]">
                            {dashboard?.pending_validations || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Preview list */}
                    <div className="space-y-2 border-t border-[#e2e8f0] pt-3">
                      {dashboard?.pending_validations_list?.length > 0 ? (
                        dashboard.pending_validations_list.map((project, idx) => (
                          <div key={project.id || idx} className="flex items-center gap-2 text-xs">
                            <span className="text-base">{projectTypeIcons[project.project_type] || '📁'}</span>
                            <span className="truncate flex-1 text-[#334155]">{project.name}</span>
                            <Badge className="bg-orange-100 text-orange-700 text-[10px]">BAT</Badge>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-[#94a3b8] italic">Rien à valider</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Support & Assistance - Tickets */}
                <Card 
                  className="card card-hover cursor-pointer border-2 border-teal-100" 
                  data-testid="stat-tickets"
                  onClick={() => navigate('/tickets')}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 bg-[#00d4aa]/10 rounded-xl flex items-center justify-center">
                          <MessageSquare className="w-5 h-5 text-[#00d4aa]" />
                        </div>
                        <div>
                          <p className="text-xs text-[#64748b]">Support & Assistance</p>
                          <p className="font-mono text-xl font-bold text-[#1a365d]">
                            {dashboard?.open_tickets || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                    {/* Description claire */}
                    <div className="bg-teal-50 rounded-lg p-2 mb-3 text-xs text-teal-700">
                      <p className="font-medium mb-1">📋 Pour vos tickets :</p>
                      <ul className="list-disc list-inside space-y-0.5 text-[10px]">
                        <li>Contrats hébergement/maintenance</li>
                        <li>Demandes de documents</li>
                        <li>Questions au service client</li>
                      </ul>
                    </div>
                    {/* Preview list */}
                    <div className="space-y-2 border-t border-[#e2e8f0] pt-3">
                      {dashboard?.open_tickets_list?.length > 0 ? (
                        <>
                          {dashboard.open_tickets_list.map((ticket, idx) => (
                            <div key={ticket.id || idx} className="flex items-center gap-2 text-xs">
                              <MessageSquare className="w-3 h-3 text-[#64748b]" />
                              <span className="truncate flex-1 text-[#334155]">{ticket.subject}</span>
                              <Badge className={cn(
                                "text-[10px]",
                                ticket.priority === 'urgent' ? "bg-red-100 text-red-700" :
                                ticket.priority === 'high' ? "bg-orange-100 text-orange-700" :
                                "bg-blue-100 text-blue-700"
                              )}>
                                {ticket.priority === 'urgent' ? 'Urgent' : 
                                 ticket.priority === 'high' ? 'Haute' : 'Normal'}
                              </Badge>
                            </div>
                          ))}
                          {dashboard.open_tickets > 3 && (
                            <p className="text-xs text-[#00d4aa] font-medium text-center pt-1">
                              +{dashboard.open_tickets - 3} autre{dashboard.open_tickets - 3 > 1 ? 's' : ''}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-xs text-[#94a3b8] italic">Aucun ticket ouvert</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Budget Communication Widget - Full Width */}
            <div className="mb-6">
              <BudgetComWidget />
            </div>

            {/* Project Requests Widget */}
            {projectRequests.length > 0 && (
              <Card className="card border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white" data-testid="project-requests-widget">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold text-[#1a365d]">
                        Mes demandes de projet
                      </CardTitle>
                      <p className="text-xs text-slate-500">
                        {projectRequests.filter(r => r.status === 'estimated').length} estimation(s) en attente de votre réponse
                      </p>
                    </div>
                  </div>
                  <Button 
                    onClick={() => navigate('/my-requests')}
                    variant="outline" 
                    size="sm" 
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    Voir tout <ArrowUpRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {projectRequests.slice(0, 4).map((request) => {
                      const statusConfig = {
                        pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700', icon: Clock },
                        estimated: { label: 'Estimation', color: 'bg-blue-100 text-blue-700', icon: Euro },
                        accepted: { label: 'Accepté', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
                        rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700', icon: AlertCircle },
                        converted: { label: 'Projet créé', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
                      };
                      const status = statusConfig[request.status] || statusConfig.pending;
                      const StatusIcon = status.icon;
                      
                      return (
                        <div 
                          key={request.id}
                          className={cn(
                            "p-3 sm:p-4 rounded-xl border transition-all cursor-pointer group",
                            request.status === 'estimated' 
                              ? "border-blue-300 bg-blue-50/50 hover:bg-blue-100/50" 
                              : "border-slate-200 hover:border-blue-300"
                          )}
                          onClick={() => navigate('/my-requests')}
                        >
                          <div className="flex items-start gap-2 sm:gap-3">
                            <div className={cn(
                              "w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center shrink-0",
                              request.status === 'estimated' ? "bg-blue-500 text-white" : "bg-slate-100"
                            )}>
                              <StatusIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 text-sm sm:text-base truncate">{request.title}</p>
                              <p className="text-xs text-slate-500">
                                {request.project_type} • {new Date(request.created_at).toLocaleDateString('fr-FR')}
                              </p>
                              {/* Mobile: Show status and button below */}
                              <div className="flex items-center justify-between gap-2 mt-2">
                                <div className="flex items-center gap-2">
                                  <Badge className={cn("text-xs", status.color)}>
                                    {status.label}
                                  </Badge>
                                  {request.status === 'estimated' && request.estimated_hours && (
                                    <span className="text-xs font-bold text-blue-600">{request.estimated_hours}h</span>
                                  )}
                                </div>
                                {request.status === 'estimated' && (
                                  <Button 
                                    size="sm" 
                                    className="bg-blue-600 hover:bg-blue-700 h-7 text-xs px-2 sm:h-8 sm:text-sm sm:px-3"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate('/my-requests');
                                    }}
                                  >
                                    Répondre
                                  </Button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Projects List */}
            <Card className="card" data-testid="projects-list">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold text-[#1a365d]">
                  Mes projets
                </CardTitle>
                <Button 
                  onClick={() => navigate('/my-projects')}
                  variant="ghost" 
                  size="sm" 
                  className="text-[#00d4aa]"
                >
                  Voir tout <ArrowUpRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {projects.slice(0, 5).map((project) => (
                    <div 
                      key={project.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-[#e2e8f0] hover:border-[#00d4aa]/50 transition-all cursor-pointer group"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-[#f8fafc] rounded-xl flex items-center justify-center text-2xl">
                          {projectTypeIcons[project.project_type] || '📁'}
                        </div>
                        <div>
                          <h4 className="font-medium text-[#1a365d] group-hover:text-[#00d4aa] transition-colors">
                            {project.name}
                          </h4>
                          <div className="flex items-center gap-2 mt-1">
                            {getStatusBadge(project.status)}
                            <span className="text-xs text-[#64748b]">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {project.actual_hours?.toFixed(1) || 0}h / {project.estimated_hours}h
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#1a365d]">
                          {(project.actual_hours || 0).toFixed(1)}h
                        </p>
                        <p className="text-xs text-[#64748b]">
                          Budget: {project.estimated_hours || 0}h
                        </p>
                      </div>
                    </div>
                  ))}
                  
                  {projects.length === 0 && (
                    <div className="text-center py-12 text-[#64748b]">
                      <FolderKanban className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p className="mb-4">Aucun projet pour le moment</p>
                      <Button className="btn-primary">
                        <Plus className="w-4 h-4 mr-2" />
                        Demander un devis
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Appointment Request Widget */}
            <div id="mes-rdv-section" className="mt-6">
              <AppointmentRequestWidget 
                theme={theme} 
                user={user} 
                accountManager={accountManager}
              />
            </div>

            {/* Google Drive Card - Only show if user has a drive URL */}
            {userProfile?.google_drive_url && (
              <Card className="card mt-6 border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-white" data-testid="google-drive-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
                        <HardDrive className="w-7 h-7 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#1a365d] text-lg flex items-center gap-2">
                          <FolderOpen className="w-5 h-5 text-blue-500" />
                          Votre dossier partagé
                        </h3>
                        <p className="text-sm text-[#64748b] mt-1">
                          Accédez à tous vos fichiers et livrables sur Google Drive
                        </p>
                      </div>
                    </div>
                    <a 
                      href={userProfile.google_drive_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all hover:shadow-lg"
                    >
                      <span>Ouvrir Google Drive</span>
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Social Media Stats */}
            {/* Section réseaux sociaux désactivée temporairement */}
            {false && socialStats && (
              <Card className="card mt-6" data-testid="social-stats">
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-[#1a365d] flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00d4aa]" />
                    Réseaux sociaux
                  </CardTitle>
                  <span className="text-xs text-[#64748b]">
                    Mis à jour {new Date(socialStats.lastUpdated).toLocaleDateString('fr-FR')}
                  </span>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Instagram */}
                    <div className="p-4 rounded-xl bg-gradient-to-br from-pink-50 to-purple-50 border border-pink-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-purple-500 flex items-center justify-center">
                          <Instagram className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium text-[#334155]">Instagram</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Abonnés</span>
                          <span className="font-mono font-bold text-[#1a365d]">
                            {socialStats.instagram.followers.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Croissance</span>
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            +{socialStats.instagram.growth}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Engagement</span>
                          <span className="font-medium text-[#1a365d]">{socialStats.instagram.engagement}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Facebook */}
                    <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#1877F2] flex items-center justify-center">
                          <Facebook className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium text-[#334155]">Facebook</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Abonnés</span>
                          <span className="font-mono font-bold text-[#1a365d]">
                            {socialStats.facebook.followers.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Croissance</span>
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            +{socialStats.facebook.growth}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Engagement</span>
                          <span className="font-medium text-[#1a365d]">{socialStats.facebook.engagement}%</span>
                        </div>
                      </div>
                    </div>

                    {/* LinkedIn */}
                    <div className="p-4 rounded-xl bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-[#0A66C2] flex items-center justify-center">
                          <Linkedin className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-medium text-[#334155]">LinkedIn</span>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Abonnés</span>
                          <span className="font-mono font-bold text-[#1a365d]">
                            {socialStats.linkedin.followers.toLocaleString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Croissance</span>
                          <span className="text-sm text-emerald-600 flex items-center gap-1">
                            <TrendingUp className="w-3 h-3" />
                            +{socialStats.linkedin.growth}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-[#64748b]">Engagement</span>
                          <span className="font-medium text-[#1a365d]">{socialStats.linkedin.engagement}%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>

      {/* Rating Modal */}
      <Dialog open={showRatingModal} onOpenChange={setShowRatingModal}>
        <DialogContent className="sm:max-w-md max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Star className="w-5 h-5 text-amber-500" />
              Évaluer l'équipe
            </DialogTitle>
          </DialogHeader>
          
          {ratingProject && (
            <div className="space-y-4 py-2 overflow-y-auto flex-1">
              <div className="text-center">
                <p className="text-sm text-slate-600 mb-2">
                  Comment évaluez-vous le travail sur
                </p>
                <p className="font-semibold text-lg" style={{ color: theme.colors?.primary }}>
                  {ratingProject.name}
                </p>
              </div>

              {/* Rating Criteria */}
              <div className="space-y-3">
                {[
                  { key: 'ecoute', label: "L'écoute", icon: '👂', description: 'Compréhension de vos besoins' },
                  { key: 'creativite', label: 'La créativité', icon: '🎨', description: 'Originalité des propositions' },
                  { key: 'delai', label: 'Le délai', icon: '⏱️', description: 'Respect des délais convenus' },
                  { key: 'budget', label: 'Le respect du budget', icon: '💰', description: 'Maîtrise des coûts' }
                ].map((criteria) => (
                  <div key={criteria.key} className="p-3 rounded-xl border border-slate-200 bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg">{criteria.icon}</span>
                        <div>
                          <p className="font-medium text-sm text-slate-800">{criteria.label}</p>
                          <p className="text-xs text-slate-500">{criteria.description}</p>
                        </div>
                      </div>
                      <span className="font-bold" style={{ color: theme.colors?.accent }}>
                        {ratings[criteria.key]}/5
                      </span>
                    </div>
                    <div className="flex justify-center items-center gap-1">
                      {[1, 2, 3, 4, 5].map((value) => (
                        <button
                          key={value}
                          type="button"
                          className="p-0.5 hover:scale-110 transition-transform"
                          onClick={() => setRatings(prev => ({ ...prev, [criteria.key]: value }))}
                        >
                          <Star 
                            className={cn(
                              "w-6 h-6 transition-colors",
                              ratings[criteria.key] >= value 
                                ? "fill-amber-400 text-amber-400" 
                                : "text-slate-300 hover:text-amber-200"
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Total Score */}
              <div className="text-center p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200">
                <p className="text-sm text-slate-600 mb-1">Note globale</p>
                <p className="text-2xl font-bold" style={{ color: theme.colors?.accent }}>
                  {ratings.ecoute + ratings.creativite + ratings.delai + ratings.budget}/20
                </p>
              </div>

              {/* Comment */}
              <div className="space-y-2">
                <Label>Commentaire (optionnel)</Label>
                <Textarea
                  value={ratingComment}
                  onChange={(e) => setRatingComment(e.target.value)}
                  placeholder="Partagez votre expérience..."
                  rows={2}
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex-shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => setShowRatingModal(false)}>
              Plus tard
            </Button>
            <Button 
              onClick={handleSubmitRating}
              disabled={submittingRating}
              style={{ backgroundColor: theme.colors?.accent }}
            >
              {submittingRating ? 'Envoi...' : 'Envoyer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Projects to Rate Alert */}
      {projectsToRate.length > 0 && (
        <div className="fixed bottom-6 right-6 z-50">
          <Card className="shadow-lg border-2 border-amber-200 bg-amber-50 max-w-sm animate-bounce-slow">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <Star className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-amber-800">
                    {projectsToRate.length} projet(s) à évaluer
                  </p>
                  <p className="text-sm text-amber-700 mt-1">
                    Donnez votre avis sur le travail réalisé !
                  </p>
                  <Button 
                    size="sm" 
                    className="mt-3 bg-amber-500 hover:bg-amber-600 text-white"
                    onClick={() => handleOpenRating(projectsToRate[0])}
                  >
                    Évaluer maintenant
                  </Button>
                </div>
                <button 
                  onClick={() => setProjectsToRate([])}
                  className="text-amber-600 hover:text-amber-800"
                >
                  ×
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Floating Action Button */}
      <ClientFloatingWidget />
    </div>
  );
};

export default ClientDashboard;
